import { useState } from 'react';
import { ListChecks, Shuffle } from 'lucide-react';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { PICK_MODE_LABELS, type NurseryLoad, type PickMode } from '../../../types/nursery';

/**
 * How a load is picked, on its page. "Allocate first" is the pick list of
 * allocations; "Pickers choose the lots" allocates nothing and shows each
 * tree with every place it stands at the despatch farm.
 *
 * It can change until picking starts (the server refuses after). Switching to
 * the pickers choosing gives back what the load's orders had allocated;
 * switching back allocates them from despatchable places, as confirming does.
 */

const MODES: { mode: PickMode; icon: typeof ListChecks; text: (farm: string) => string }[] = [
  {
    mode: 'allocated',
    icon: ListChecks,
    text: () => 'Each order is allocated to lots before it is picked, and pickers pick those lots.',
  },
  {
    mode: 'free',
    icon: Shuffle,
    text: (farm) => `Nothing is allocated. Pickers see every place each tree stands at ${farm}, in walk order, and choose where to take it from.`,
  },
];

export interface PickModeCardProps {
  load: NurseryLoad;
  /** Planning or released: picking may not have started. */
  editable: boolean;
  onChanged: (load: NurseryLoad) => void;
}

export function PickModeCard({ load, editable, onChanged }: PickModeCardProps) {
  const current: PickMode = load.pick_mode ?? 'allocated';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const hasOrders = (load.orders ?? []).length > 0;

  const choose = async (mode: PickMode) => {
    if (mode === current || busy) return;
    if (mode === 'free' && hasOrders
      && !window.confirm('Anything this load\'s orders have allocated will be released back to free stock. Let the pickers choose the lots?')) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await nurseryService.setLoadPickMode(load.id, mode);
      if (res.success) {
        onChanged(res.data);
        const released = res.data.released_trees ?? 0;
        setNote(mode === 'free'
          ? (released ? `${released.toLocaleString()} allocated tree${released === 1 ? '' : 's'} released back to free stock.` : null)
          : (hasOrders ? 'The orders have been allocated from despatchable places where there was stock.' : null));
      } else {
        setError(errorMessage(res, 'Could not change how this load is picked'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not change how this load is picked'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4" aria-labelledby="load-pick-mode">
      <h2 id="load-pick-mode" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">How it is picked</h2>
      {editable ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="How this load is picked">
          {MODES.map(({ mode, icon: Icon, text }) => {
            const on = mode === current;
            return (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={busy}
                onClick={() => choose(mode)}
                className={cn('text-left rounded-lg border p-3 text-sm transition-colors min-w-0',
                  on ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-neutral-200 hover:border-primary-300')}
              >
                <span className="flex items-center gap-2 font-medium text-neutral-900">
                  <Icon className="h-4 w-4 flex-shrink-0" /> {PICK_MODE_LABELS[mode]}
                </span>
                <span className="block mt-1 text-xs text-neutral-600 break-words">{text(load.despatch_farm_name)}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="mt-2 text-sm text-neutral-800 break-words">
          <span className="font-medium">{PICK_MODE_LABELS[current]}.</span>{' '}
          {MODES.find((m) => m.mode === current)!.text(load.despatch_farm_name)}
        </p>
      )}
      {note && <p className="mt-2 text-sm text-green-800 break-words">{note}</p>}
      {error && <p className="mt-2 text-sm text-red-700 break-words">{error}</p>}
    </section>
  );
}

export default PickModeCard;
